/**
 * The compact, borderless player row of the redesigned add-players drawer —
 * the PC2 search-result row, and the PC5/PC6 Watchlists / My Roster row.
 *
 * One line: initials avatar · name over a meta caption · inline "GPR n" info
 * pill · a compact solid-accent **+ Add**. A player already in this pipeline
 * mutes the row and states a plain "In list" in place of the action (PC2's
 * ALREADY IN THIS PIPELINE group, PC5's in-list rows). No card border — the
 * frames draw these as flat rows, unlike the bordered suggestion card.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/AddPlayersRow/CompactPlayerRow
 */

import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import { Plus } from "lucide-react-native";
import React, { memo } from "react";

import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import { TonePill } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link CompactPlayerRowView}. */
export interface CompactPlayerRowViewProps {
  /** Player id — the add target and the row's test identity. */
  readonly playerId: string;
  /** Display name. */
  readonly name: string;
  /** Pre-composed meta caption (varies per pool — see addPlayersRowMeta). */
  readonly meta: string;
  /** GPR score for the inline info pill; `null` omits the pill. */
  readonly gpr?: string | null;
  /** Whether the player already sits in this pipeline (muted, "In list"). */
  readonly isInList?: boolean;
  /** Whether an add for this player is in flight. */
  readonly isAdding?: boolean;
  /** Add this player to the pipeline. Absent hides the action entirely. */
  readonly onAdd?: () => void;
}

/**
 * Render one compact drawer row.
 * @param root0 - Component props.
 * @param root0.playerId - Player id.
 * @param root0.name - Display name.
 * @param root0.meta - Meta caption.
 * @param root0.gpr - GPR score for the pill.
 * @param root0.isInList - Whether the player is already in this pipeline.
 * @param root0.isAdding - Whether an add is in flight.
 * @param root0.onAdd - Add callback.
 */
const CompactPlayerRowView = ({
  playerId,
  name,
  meta,
  gpr = null,
  isInList = false,
  isAdding = false,
  onAdd,
}: CompactPlayerRowViewProps) => (
  <Stack
    testID={TEST_ID_ADD_PLAYERS_PANEL.ROW(playerId)}
    direction="horizontal"
    className={`items-center gap-2 py-1 ${isInList ? "opacity-60" : ""}`.trim()}
  >
    {/* The frames draw the row avatar as a dark disc — the inverse
        surface/content pairing the Player Card v7 identity established. */}
    <Avatar size="sm" className="shrink-0 bg-surface-inverse">
      <AvatarFallbackText className="text-content-inverse">
        {name}
      </AvatarFallbackText>
    </Avatar>

    <Stack className="min-w-0 flex-1 gap-px">
      <Text variant="body-strong" className="text-content-primary" isTruncated>
        {name}
      </Text>
      {meta === "" ? null : (
        <Text variant="caption" className="text-content-muted" isTruncated>
          {meta}
        </Text>
      )}
    </Stack>

    {gpr === null ? null : (
      <TonePill
        tone="info-soft"
        label={`GPR ${gpr}`}
        textVariant="micro"
        className="shrink-0 px-2 py-micro"
      />
    )}

    {isInList ? (
      <Text
        testID={TEST_ID_ADD_PLAYERS_PANEL.ROW_IN_LIST(playerId)}
        variant="caption"
        className="shrink-0 text-content-muted"
      >
        In list
      </Text>
    ) : onAdd === undefined ? null : (
      <Button
        testID={TEST_ID_ADD_PLAYERS_PANEL.ROW_ADD(playerId)}
        accessibilityLabel={`Add ${name} to pipeline`}
        label="Add"
        icon={Plus}
        tone="accent"
        size="sm"
        className="shrink-0 rounded-sm px-4"
        isLoading={isAdding}
        onPress={onAdd}
      />
    )}
  </Stack>
);

CompactPlayerRowView.displayName = "CompactPlayerRowView";

export default memo(CompactPlayerRowView);
